import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import "./LandingPage.css";
import api from "../../api";
import Seo from "../Seo/Seo";
import { DEFAULT_OG_IMAGE } from "../../utils/ogImage";
import { homeSeo } from "../../utils/pageSeo";
import LandingNav from "./components/LandingNav";
import HomeView from "./views/HomeView";
import BusinessPortalView from "./views/BusinessPortalView";
import ConsumerSignInView from "./views/ConsumerSignInView";
import { isBusinessUser as checkBusinessUser } from "../../utils/postLoginPath";

/**
 * Marketing landing page — home ('/') view, plus the business-portal and
 * consumer-signin sub-views, switched client-side (not separate routes).
 * Other pages (e.g. Header's "Sign in" / "Become a partner") can deep-link
 * into a sub-view via navigate("/", { state: { view: "signin" } }).
 *
 * @component
 */
const LandingPage = () => {
  const { user, handleLoginSuccess, handleLogout } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeView, setActiveView] = useState(location.state?.view || "home"); // 'home' | 'business' | 'signin'
  const [signInMode, setSignInMode] = useState("login"); // 'login' | 'register' — which tab ConsumerSignInView opens on
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);

  // Business users (organizers/admins) get routed straight to the /admin
  // dashboard. Regular members stay on the marketing homepage — the member
  // area (/home) is disabled pending a rewrite (see Routes/routes.js).
  const isBusinessUser = checkBusinessUser(user);

  useEffect(() => {
    if (isBusinessUser) {
      navigate("/admin");
    }
  }, [isBusinessUser, navigate]);

  // Shared with LandingNav's location dropdown, so it's owned here rather
  // than inside HomeView (LandingNav is HomeView's sibling, not its parent).
  // Only cities that actually have places — the location dropdown (and the
  // Hero / footer city lists that share this state) should never offer a
  // city with nothing to show.
  useEffect(() => {
    api
      .getAllCities({ withPlaces: true })
      .then((res) => setCities(res.data))
      .catch((err) => console.error("Error fetching cities for landing page:", err));
  }, []);

  useEffect(() => {
    if (!selectedCity && cities.length > 0) {
      const southampton = cities.find((c) => c.name.toLowerCase() === "southampton");
      setSelectedCity((southampton || cities[0]).name);
    }
  }, [cities, selectedCity]);

  if (isBusinessUser) return null; // Return null while redirecting

  const goHome = () => setActiveView("home");
  const goBusiness = () => setActiveView("business");
  const goSignin = (mode = "login") => {
    setSignInMode(mode);
    setActiveView("signin");
  };

  return (
    <div className="landing-page">
      {/* Explicit, self-owned homepage metadata rather than relying only on
          DefaultSeo's fallback (rendered once at the app root) — same
          title/description copy DefaultSeo already used, just now emitted
          intentionally from "/" itself with type="website" (DefaultSeo's
          value; Seo's own default is "article", correct for Guides/Places
          but wrong here — see Seo.js). */}
      <Seo
        title={homeSeo().title}
        description={homeSeo().description}
        path="/"
        image={DEFAULT_OG_IMAGE}
        type="website"
      />
      <LandingNav
        activeView={activeView}
        onGoHome={goHome}
        onGoBusiness={goBusiness}
        onGoSignin={goSignin}
        cities={cities}
        selectedCity={selectedCity}
        setSelectedCity={setSelectedCity}
        user={user}
        onLogout={handleLogout}
      />
      {activeView === "home" && (
        <HomeView
          onGoBusiness={goBusiness}
          onGoSignin={goSignin}
          cities={cities}
          selectedCity={selectedCity}
        />
      )}
      {activeView === "business" && (
        <BusinessPortalView onLoginSuccess={handleLoginSuccess} />
      )}
      {activeView === "signin" && (
        <ConsumerSignInView onLoginSuccess={handleLoginSuccess} onGoHome={goHome} initialMode={signInMode} />
      )}
    </div>
  );
};

export default LandingPage;
